/**
 * Fetch utility with automatic App Check and Auth token attachment
 * 
 * This wraps the standard fetch API to automatically attach both App Check tokens
 * and Firebase Auth bearer tokens to all requests going to Firebase Cloud Functions.
 */

import { getToken } from 'firebase/app-check';
import { getAppCheck, isAppCheckEnabled, getFirebaseAuth } from '../config/firebase';

/**
 * Fetch with App Check and Auth tokens automatically attached
 * 
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns Promise<Response>
 */
export async function appCheckFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Clone options to avoid mutating the original
  const fetchOptions = { ...options };
  
  // Initialize headers if not present
  if (!fetchOptions.headers) {
    fetchOptions.headers = {};
  }
  
  // Convert headers to Headers object if it's a plain object
  const headers = new Headers(fetchOptions.headers);
  
  // Attach Firebase Auth Bearer Token
  try {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    
    if (user) {
      // Get the current user's ID token
      const idToken = await user.getIdToken(/* forceRefresh */ false);
      
      // Attach as Bearer token
      headers.set('Authorization', `Bearer ${idToken}`);
      
      console.debug('✅ Auth Bearer token attached to request:', {
        url: url.replace(/\?.*/,  ''), // Remove query params for logging
        tokenPreview: idToken.substring(0, 20) + '...',
      });
    } else {
      console.warn('⚠️  No authenticated user - request will be sent without auth token');
    }
  } catch (error) {
    console.error('❌ Failed to get Auth token:', error);
    console.warn('⚠️  Request will be sent without Auth token and may be rejected.');
  }
  
  // Attach App Check token if App Check is enabled
  if (isAppCheckEnabled()) {
    try {
      const appCheck = getAppCheck();
      if (appCheck) {
        console.debug('🔐 Requesting App Check token for:', url.replace(/\?.*/, ''));
        
        // Get a fresh App Check token
        const appCheckToken = await getToken(appCheck, /* forceRefresh */ false);
        
        // Attach the token to the request headers
        headers.set('X-Firebase-AppCheck', appCheckToken.token);
        
        console.debug('✅ App Check token attached to request:', {
          url: url.replace(/\?.*/,  ''), // Remove query params for logging
          tokenPreview: appCheckToken.token.substring(0, 20) + '...',
        });
      }
    } catch (error) {
      console.error('❌ Failed to get App Check token:', error);
      console.error('   Error code:', (error as any).code);
      console.error('   Error message:', (error as any).message);
      
      if ((error as any).message?.includes('quota')) {
        console.warn('⚠️  ReCAPTCHA quota exceeded');
        console.warn('   This usually resolves automatically. Try again in a few minutes.');
      } else if ((error as any).message?.includes('domain')) {
        console.warn('⚠️  Domain not authorized in Firebase App Check');
        console.warn('   Current domain:', window.location.hostname);
        console.warn('   Fix: Add this domain in Firebase Console > App Check > Apps');
      } else if ((error as any).message?.includes('key')) {
        console.warn('⚠️  Invalid or mismatched ReCAPTCHA site key');
        console.warn('   Verify VITE_RECAPTCHA_SITE_KEY matches Firebase Console');
      }
      
      console.warn('⚠️  Request will be sent without App Check token and may be rejected.');
      console.warn('   Backend error will be: "firebase-app-check-token-is-invalid"');
      // Continue with the request even if token fetch fails
      // The backend will reject it, but we want to see the error
    }
  } else {
    console.debug('⚠️  App Check not enabled, sending request without token');
  }
  
  // Update options with headers
  fetchOptions.headers = headers;
  
  // Make the fetch request
  return fetch(url, fetchOptions);
}

/**
 * Convenience wrapper for GET requests with App Check
 */
export async function appCheckGet(url: string): Promise<Response> {
  return appCheckFetch(url, { method: 'GET' });
}

/**
 * Convenience wrapper for POST requests with App Check
 */
export async function appCheckPost(
  url: string,
  body?: any
): Promise<Response> {
  return appCheckFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Convenience wrapper for PUT requests with App Check
 */
export async function appCheckPut(
  url: string,
  body?: any
): Promise<Response> {
  return appCheckFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Convenience wrapper for DELETE requests with App Check
 */
export async function appCheckDelete(url: string): Promise<Response> {
  return appCheckFetch(url, { method: 'DELETE' });
}

